import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import FloatingInput from "../common/FloatingInput";
import { toast } from "react-toastify";
import {
  censusBillingRules,
  getCensusPayors,
  censusGroupPlanAction,
} from "../services/soar";

function CensusClone({ currentRule, show, setShow }) {
  const [setupData, setSetupData] = useState({});
  const [payor, setPayor] = useState("");
  const [groupPlan, setGroupPlan] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [confirmData, setConfirmData] = useState(null);
  const navigate = useNavigate();
  const applicationId =
    process.env.REACT_APP_ENVIRONMENT === "Production"
      ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
      : "58990f83-8566-4ba8-b4b5-584a64e4cba1";

  useEffect(() => {
    getSetupData();
  }, []);

  const getSetupData = async () => {
    const payorResponse = await getCensusPayors();
    const groupPlansResponse = await censusGroupPlanAction({
      command: "query",
      keyword: "viewall",
      application: applicationId,
    });
    setSetupData({
      ...setupData,
      payors: payorResponse
        .filter((payor) => payor.active)
        .map((payor) => ({
          _id: payor.id,
          name: payor.name,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
      groupPlans: groupPlansResponse.data
        .filter((groupPlan) => groupPlan.active)
        .map((groupPlan) => ({
          _id: groupPlan.id,
          name: groupPlan.name,
          payorId: groupPlan.payorId,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    });
  };

  const handleClone = async () => {
    if (!payor || !groupPlan || !effectiveDate) {
      toast.error("Please select a Payor, Group Plan, and Effective Date.");
      return;
    }

    if (
      payor === currentRule.payorId &&
      groupPlan === currentRule.groupPlanId
    ) {
      toast.error(
        "Payor and Group Plan cannot be the same as the current rule."
      );
      return;
    }

    try {
      // Check for existing Billing Rule
      const existingRuleResponse = await censusBillingRules({
        command: "query",
        application: applicationId,
        keyword: "allinterm",
        searchTerms: {
          payor: payor,
          groupPlan: groupPlan,
          serviceConfiguration: currentRule.serviceConfigurationId,
        },
      });

      if (existingRuleResponse.data?.filter((r) => r.active)?.length > 0) {
        // Existing rule found
        toast.info(
          "A Billing Rule already exists for the selected Payor and Group Plan. Please Confirm"
        );

        setConfirmData({
          activeRule: existingRuleResponse.data.filter(
            (r) => r.active && (!r.expiration || r.expiration === "")
          )[0],
          payor,
          groupPlan,
          effectiveDate,
        });
        return;
        // const existingRule = existingRuleResponse.data[0];
        // const confirmOverwrite = window.confirm(
        //   "A Billing Rule already exists for the selected Payor and Group Plan. Publishing will overwrite the existing configuration. Do you want to proceed?"
        // );

        // if (!confirmOverwrite) {
        //   return;
        // }

        // Expire existing rule
        // await censusBillingRules({
        //   command: "expire",
        //   application: currentRule.applicationId,
        //   id: existingRule.id,
        //   expiration: new Date(
        //     new Date(effectiveDate).setDate(
        //       new Date(effectiveDate).getDate() - 1
        //     )
        //   ).toISOString(),
        // });
      } else {
        const clonedResponse = await censusBillingRules({
          command: "insert",
          application: applicationId,
          name: currentRule.name,
          type: currentRule.type,
          payor: payor,
          groupPlan: groupPlan,
          serviceConfiguration: currentRule.serviceConfigurationId,
          effective: effectiveDate,
          expiration: "",
          configuration: currentRule.configuration,
        });

        if (clonedResponse.code === "success") {
          toast.success("Rule cloned successfully!");
          setShow(false);
          onClose();
          navigate(`/rev/rules/${clonedResponse.id}`);
        } else {
          toast.error(`Cloning failed. ${clonedResponse.message}`);
        }
      }
    } catch (error) {
      toast.error("Error cloning rule: " + error.message);
    }
  };

  const overrideClone = async () => {
    //expire existing rule 1 day before effective date
    const expireResponse = await censusBillingRules({
      command: "expire",
      application: applicationId,
      id: confirmData.activeRule.id,
      expiration: new Date(
        new Date(confirmData.effectiveDate).setDate(
          new Date(confirmData.effectiveDate).getDate() - 1
        )
      ).toISOString(),
    });

    if (expireResponse.code !== "success") {
      toast.error(`Expiration failed. ${expireResponse.message}`);
      return;
    }

    //clone new rule
    const clonedResponse = await censusBillingRules({
      command: "insert",
      application: applicationId,
      name: currentRule.name,
      type: currentRule.type,
      payor: payor,
      groupPlan: groupPlan,
      serviceConfiguration: currentRule.serviceConfigurationId,
      effective: effectiveDate,
      expiration: "",
      configuration: currentRule.configuration,
    });

    if (clonedResponse.code === "success") {
      toast.success("Rule cloned successfully!");
      setShow(false);
      onClose();
      navigate(`/rev/rules/${clonedResponse.id}`);
    } else {
      toast.error(`Cloning failed. ${clonedResponse.message}`);
    }
  };

  const onClose = () => {
    setPayor("");
    setGroupPlan("");
    setEffectiveDate("");
    setConfirmData(null);
    setShow(false);
  };

  return (
    <Modal show={show} onHide={onClose} animation={false} size="lg">
      <Modal.Header closeButton className="bg-safy-info text-white">
        <Modal.Title>Clone Census Rule</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FloatingInput
          type="select"
          name="payor"
          label="Payor"
          value={payor}
          options={setupData.payors}
          onChange={(e) => setPayor(e.target.value)}
        />
        <FloatingInput
          type="select"
          name="groupPlan"
          label="Group Plan"
          value={groupPlan}
          options={setupData?.groupPlans?.filter((gp) =>
            payor !== "" ? gp.payorId === payor : gp
          )}
          onChange={(e) => setGroupPlan(e.target.value)}
        />
        <FloatingInput
          type="date"
          name="effectiveDate"
          label="Effective Date"
          value={effectiveDate}
          onChange={(e) => setEffectiveDate(e.target.value)}
        />
        {confirmData && (
          <div className="alert alert-warning mt-3">
            <strong>Warning:</strong> A Billing Rule already exists for the
            selected Payor and Group Plan. Publishing will expire the existing
            configuration.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="btn btn-danger" onClick={onClose}>
          Cancel
        </div>
        {!confirmData && (
          <div className="btn btn-success" onClick={handleClone}>
            Clone Rule
          </div>
        )}
        {confirmData && (
          <div className="btn btn-warning" onClick={overrideClone}>
            Confirm Clone
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default CensusClone;
